import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { TopBar } from "./TopBar";
import { Sidebar } from "./Sidebar";
import { Breadcrumbs } from "./Breadcrumbs";
import { BreadcrumbProvider } from "./BreadcrumbContext";
import { useUIStore } from "@/shared/store/ui";
import { cn } from "@/shared/utils/cn";
import { AttendanceGuardModal } from "@/modules/attendance/components/AttendanceGuardModal";
import { SessionStartGate } from "@/modules/timetracking";
import { ClientEmailGate } from "@/modules/clientportal/components/ClientEmailGate";
import { RegularisationPolicyBanner } from "@/modules/regularisations/components/RegularisationPolicyBanner";

export function AppLayout({ children }: { children: ReactNode }) {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const mobileOpen = useUIStore((s) => s.mobileSidebarOpen);
  const closeMobile = useUIStore((s) => s.closeMobileSidebar);
  const location = useLocation();

  // Close drawer on route change.
  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  // Lock body scroll while drawer open.
  useEffect(() => {
    if (!mobileOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileOpen]);

  return (
    <BreadcrumbProvider>
      <div
        className={cn(
          "app",
          collapsed && "is-collapsed",
          mobileOpen && "is-mobile-open",
        )}
      >
        <TopBar />
        <Sidebar />
        {mobileOpen && (
          <div
            className="app-scrim"
            role="presentation"
            onClick={closeMobile}
          />
        )}
        <main className="main">
          <Breadcrumbs />
          <RegularisationPolicyBanner />
          {children}
        </main>
        <AttendanceGuardModal />
        <SessionStartGate />
        <ClientEmailGate />
      </div>
    </BreadcrumbProvider>
  );
}
